<template>
  <div class="form tab-content">
    <v-layout column>
      <v-layout row align-end justify-space-between wrap v-if="!disabled">
        <input-field
          value=""
          :label="$t('NewEquipment')"
          size="large"
          inputType="group"
        />

        <input-field
          v-model="newSplitterLabel"
          :label="$tc('Label', 1)"
          inputType="text"
          size="45"
          :disabled="disabled"
          class="splitter-input-field"
        />

        <input-field
          :value="selectedSplitterMaterial"
          :label="$tc('Equipment', 1)"
          inputType="other"
          size="45"
        >
          <treeselect
            v-model="selectedSplitterMaterial"
            :options="newSplitterMaterialOptions"
            :load-options="loadNewSplitterMaterialOptions"
            :normalizer="normalizer"
            :disabled="disabled || networkLevel === null"
            class="form-input-select"
            valueFormat="object"
            disableFuzzyMatching
          />
        </input-field>

        <v-btn
          @click="addSplitter"
          :disabled="
            !newSplitterLabel ||
              newSplitterLabel === '' ||
              !selectedSplitterMaterial ||
              loading
          "
          icon
          x-small
          text
          color="accent"
          class="mb-2 mr-5"
        >
          <v-icon>add_circle</v-icon>
        </v-btn>
      </v-layout>

      <v-layout row style="flex-wrap: nowrap" align-center>
        <v-btn
          @click="getObjectSplitters"
          :disabled="loading"
          icon
          text
          x-small
          color="accent"
          class="mt-1"
        >
          <v-icon>refresh</v-icon>
        </v-btn>

        <input-field
          value=""
          :label="$t('ExistingSplitters')"
          size="large"
          inputType="group"
        />
      </v-layout>

      <v-layout
        column
        class="splitters-container scroll-fire-fox"
        style="position: relative"
      >
        <splitters-table
          :splitters="splitters"
          :splitter-material-options="allSplitterOptions"
          :disabled="disabled"
          :loading="loading"
          :updated="updateIndex"
          @edit-splitter="editSplitter($event.index, $event.data)"
          @delete-splitter="removeSplitter"
        />

        <loading-spinner
          :active.sync="loading"
          :is-full-page="false"
          :height="40"
          :width="40"
          color="accent"
          background-color="transparent"
        />
      </v-layout>

      <v-layout row justify-center class="mt-2">
        <v-icon color="#515358" size="12" class="pr-1">warning</v-icon>
        <div class="message">{{ $t("TabContentSavingWhileEditing") }}</div>
      </v-layout>
    </v-layout>
  </div>
</template>

<script>
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import { mapActions } from "vuex";
import LoadingSpinner from "vue-loading-overlay";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import SplittersTable from "@/components/Home/Forms/_SpecialTabs/Splitters/SplittersTable";

export default {
  name: "ClosuresFormSplittersTab",

  components: {
    SplittersTable,
    InputField,
    LoadingSpinner
  },

  mixins: [checkPermissionsMixin],

  props: {
    networkLevel: {
      required: true
    },

    objectEndpoint: {
      type: String,
      required: true
    },

    objectUuid: {
      type: String
    },

    objectId: {
      type: Number
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedSplitterMaterial: undefined,
      newSplitterMaterialOptions: null,
      newSplitterLabel: null,

      splitters: [],

      loading: false,

      allSplitterOptions: [],

      updateIndex: 0
    };
  },

  watch: {
    networkLevel() {
      this.newSplitterMaterialOptions = null;
    }
  },

  mounted() {
    this.loadAllSplitterMaterials();

    if (this.objectUuid) {
      this.getObjectSplitters();
    }
  },

  methods: {
    ...mapActions([
      "fetchStockMaterialsList",
      "fetchSplittersInObject",
      "createSplitter",
      "updateSplitter",
      "deleteSplitter"
    ]),

    //region Material Options methods

    async loadNewSplitterMaterialOptions({ callback }) {
      try {
        this.newSplitterMaterialOptions = await this.fetchStockMaterialsList({
          workspace_uuid: this.$store.getters.workspace.uuid,
          material_family_id: "sp",
          network_level_id: this.networkLevel,
          append: "family_specific_fields"
        });
      } catch (e) {
        console.log(e);
      } finally {
        callback();
      }
    },

    async loadAllSplitterMaterials() {
      try {
        this.allSplitterOptions = await this.fetchStockMaterialsList({
          material_family_id: "sp",
          append: "family_specific_fields"
        });
      } catch (e) {
        console.log(e);
      }
    },

    normalizer(node) {
      let label = node.sm_description;
      if (node.sm_short_description) {
        label = node.sm_short_description;
      }
      if (node.family_specific_fields) {
        let ratio =
          node.family_specific_fields.sms_in_ports +
          ":" +
          node.family_specific_fields.sms_out_ports;
        label += " (" + ratio + ")";
      }
      return {
        id: node.sm_id,
        label: label
      };
    },

    //endregion

    //region Manage Splitters

    async getObjectSplitters() {
      this.loading = true;
      try {
        this.splitters = await this.fetchSplittersInObject({
          objectEndpoint: this.objectEndpoint,
          objectUUID: this.objectUuid
        });
        this.updateIndex++;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async addSplitter() {
      let splitter = this.prepareNewSplitterData();

      this.loading = true;
      try {
        let newSplitter = await this.createSplitter(splitter);
        this.openPanel = null;
        this.splitters.push(newSplitter);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    prepareNewSplitterData() {
      let splitter = {
        material_id: this.selectedSplitterMaterial.sm_id,
        label: this.newSplitterLabel
      };

      if (this.objectEndpoint === "closures") {
        splitter.closure_id = this.objectId;
      } else if (this.objectEndpoint === "technicallocals") {
        splitter.technical_local_id = this.objectId;
      }

      return splitter;
    },

    async editSplitter(index, updateInfo) {
      this.loading = true;
      try {
        await this.updateSplitter({
          sp_uuid: updateInfo.sp_uuid,
          sp_label: updateInfo.sp_label
        });
        await this.getObjectSplitters();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async removeSplitter(sp_id) {
      this.loading = true;
      const splitter = this.splitters.find(splitter => splitter.sp_id === sp_id);
      try {
        await this.deleteSplitter(splitter.sp_uuid);
        await this.getObjectSplitters();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    }
    //endregion
  }
};
</script>

<style scoped>
.splitters-container {
  min-height: 50px;
  max-width: 100%;
  max-height: 45vh;
  overflow: auto;
  margin: 0 20px;
}

.message {
  color: #515358;
  padding: 4px 0;
  font-size: 10px;
}
</style>

<style>
.splitters-container .v-expansion-panel__header {
  padding: 10px;
  min-height: unset;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Splitters/SplittersTab.vue